import { describeConflict, hostOf, isRootId, type BookmarkRow } from '@bsync/shared';
import { A, useSearchParams } from '@solidjs/router';
import { For, Match, Show, Switch, createEffect, createMemo, createSignal, on, onCleanup } from 'solid-js';
import { ConfirmDialog, EditDialog, MoveDialog, type EditTarget } from '../components/Dialog';
import { canDropInto, expand } from '../components/FolderTree';
import { IconEdit, IconExternal, IconFolder, IconGrip, IconMove, IconPlus, IconSearch, IconTrash } from '../components/Icons';
import { clearConflicts, edit, state } from '../lib/app';
import { countIn, pathTo, search, tileFor, titleOf } from '../lib/bookmarks';
import { config } from '../lib/config';
import { dnd, draggedIds, endDrag, startDrag } from '../lib/dnd';
import { toast } from '../lib/toasts';

type DropWhere = 'before' | 'after' | 'into';

export default function BookmarksPage() {
  const [params, setParams] = useSearchParams();
  const folder = () => (typeof params.folder === 'string' ? params.folder : 'root:toolbar');
  const query = () => (typeof params.q === 'string' ? params.q : '');
  const tree = state.tree;
  const exists = () => isRootId(folder()) || !!tree()?.parentOf.has(folder());
  const children = () => tree()?.childrenOf.get(folder()) ?? [];
  const hits = createMemo(() => (tree() && query().trim() ? search(tree()!, query()) : []));
  const libraryEmpty = () => (tree()?.parentOf.size ?? 0) === 0;

  const [selected, setSelected] = createSignal<Set<string>>(new Set());
  const [editTarget, setEditTarget] = createSignal<EditTarget | null>(null);
  const [moveIds, setMoveIds] = createSignal<string[] | null>(null);
  const [confirmDelete, setConfirmDelete] = createSignal<string[] | null>(null);
  const [indicator, setIndicator] = createSignal<{ id: string; where: DropWhere } | null>(null);
  const [showConflicts, setShowConflicts] = createSignal(params.conflicts === '1');
  const [draft, setDraft] = createSignal(query());

  // Selection is per folder; drop ids that disappear (deleted or moved elsewhere by sync).
  createEffect(on(folder, () => setSelected(new Set<string>())));
  createEffect(() => {
    const live = new Set(children());
    const sel = selected();
    if ([...sel].some((id) => !live.has(id))) setSelected(new Set([...sel].filter((id) => live.has(id))));
  });
  createEffect(() => {
    if (tree() && exists() && !isRootId(folder())) expand(pathTo(tree()!, folder()).slice(0, -1));
  });

  let searchTimer: ReturnType<typeof setTimeout> | undefined;
  const onSearch = (v: string) => {
    setDraft(v);
    clearTimeout(searchTimer);
    searchTimer = setTimeout(() => setParams({ q: v || undefined }, { replace: true }), 150);
  };
  onCleanup(() => clearTimeout(searchTimer));

  const openFolder = (id: string) => {
    setDraft('');
    setParams({ folder: id, q: undefined, conflicts: undefined });
  };

  const toggleSelect = (id: string) => {
    const next = new Set(selected());
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };

  const requestDelete = (ids: string[]) => {
    const t = tree();
    if (!t) return;
    const heavy = ids.some((id) => t.rows.get(id)?.kind === 'folder' && (t.childrenOf.get(id)?.length ?? 0) > 0);
    if (heavy || ids.length > 1) setConfirmDelete(ids);
    else void doDelete(ids);
  };

  const doDelete = async (ids: string[]) => {
    const removed = await edit((ed) => ed.remove(ids));
    setSelected(new Set<string>());
    if (removed?.length) {
      toast(ids.length === 1 ? `Deleted “${titleOf(tree()!, ids[0]!) || 'item'}”.` : `Deleted ${ids.length} items.`, {
        action: { label: 'Undo', run: () => void edit((ed) => ed.restore(removed)) },
      });
    }
  };

  const save = async (t: EditTarget) => {
    if (t.mode === 'edit') await edit((ed) => ed.update(t.id!, t.kind === 'bookmark' ? { title: t.title, url: t.url } : { title: t.title }));
    else if (t.kind === 'folder') await edit((ed) => ed.createFolder(t.parentId, t.title));
    else await edit((ed) => ed.createBookmark(t.parentId, t.title, t.url));
  };

  const newItem = (kind: 'bookmark' | 'folder') =>
    setEditTarget({ mode: kind === 'folder' ? 'create-folder' : 'create-bookmark', kind, parentId: exists() ? folder() : 'root:toolbar', title: '', url: '' });

  const editItem = (row: BookmarkRow) =>
    setEditTarget({ mode: 'edit', id: row.id, kind: row.kind === 'folder' ? 'folder' : 'bookmark', parentId: folder(), title: row.title, url: row.url ?? '' });

  // ------------------------------------------------------------ drag and drop

  const dragIdsFor = (id: string) => (selected().has(id) ? children().filter((c) => selected().has(c)) : [id]);

  const onRowDragOver = (e: DragEvent, row: BookmarkRow) => {
    const ids = dnd.dragging();
    if (!ids || ids.includes(row.id)) return;
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const ratio = (e.clientY - rect.top) / rect.height;
    let where: DropWhere = ratio < 0.5 ? 'before' : 'after';
    if (row.kind === 'folder' && ratio > 0.25 && ratio < 0.75 && canDropInto(row.id, ids)) where = 'into';
    if (where !== 'into' && !canDropInto(folder(), ids)) return;
    e.preventDefault();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
    const cur = indicator();
    if (cur?.id !== row.id || cur.where !== where) setIndicator({ id: row.id, where });
  };

  const onRowDrop = async (e: DragEvent, row: BookmarkRow) => {
    e.preventDefault();
    const ind = indicator();
    setIndicator(null);
    const ids = draggedIds(e);
    endDrag();
    if (!ids || !ind) return;
    if (ind.where === 'into') {
      await edit((ed) => ed.move(ids, row.id));
      return;
    }
    const idx = children().indexOf(row.id);
    await edit((ed) => ed.move(ids, folder(), ind.where === 'before' ? idx : idx + 1));
  };

  const onListDrop = async (e: DragEvent) => {
    if (e.defaultPrevented) return;
    e.preventDefault();
    const ids = draggedIds(e);
    endDrag();
    setIndicator(null);
    if (ids && canDropInto(folder(), ids)) await edit((ed) => ed.move(ids, folder()));
  };

  const onRowKey = (e: KeyboardEvent, row: BookmarkRow) => {
    if (e.target !== e.currentTarget) return;
    if (e.key === 'Enter') {
      if (row.kind === 'folder') openFolder(row.id);
      else if (row.url) window.open(row.url, '_blank', 'noopener');
    } else if (e.key === 'Delete' || e.key === 'Backspace') {
      e.preventDefault();
      requestDelete([row.id]);
    } else if (e.key === ' ') {
      e.preventDefault();
      toggleSelect(row.id);
    } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const rows = [...document.querySelectorAll<HTMLElement>('.list .row')];
      const i = rows.indexOf(e.currentTarget as HTMLElement);
      rows[e.key === 'ArrowDown' ? i + 1 : i - 1]?.focus();
    }
  };

  // ------------------------------------------------------------ render

  const Row = (props: { row: BookmarkRow; path?: string[] }) => {
    const r = () => props.row;
    const tile = () => tileFor(r().url);
    const ind = () => (indicator()?.id === r().id ? indicator()!.where : null);
    return (
      <div
        class="row"
        classList={{
          selected: selected().has(r().id),
          'drop-before': ind() === 'before',
          'drop-after': ind() === 'after',
          'drop-into': ind() === 'into',
          dragging: !!dnd.dragging()?.includes(r().id),
          separator: r().kind === 'separator',
        }}
        tabIndex={0}
        draggable={!props.path}
        onDragStart={(e) => startDrag(e, dragIdsFor(r().id))}
        onDragEnd={() => {
          endDrag();
          setIndicator(null);
        }}
        onDragOver={(e) => !props.path && onRowDragOver(e, r())}
        onDragLeave={(e) => {
          if (!(e.currentTarget as HTMLElement).contains(e.relatedTarget as Node)) setIndicator(null);
        }}
        onDrop={(e) => !props.path && onRowDrop(e, r())}
        onKeyDown={(e) => onRowKey(e, r())}
        onDblClick={() => r().kind === 'folder' && openFolder(r().id)}
      >
        <Show when={!props.path}>
          <span class="grip" aria-hidden="true">
            <IconGrip />
          </span>
          <input type="checkbox" class="check" checked={selected().has(r().id)} onChange={() => toggleSelect(r().id)} aria-label={`Select ${r().title}`} />
        </Show>
        <Switch>
          <Match when={r().kind === 'separator'}>
            <span class="sep-line" aria-label="Separator" />
          </Match>
          <Match when={r().kind === 'folder'}>
            <span class="tile folder">
              <IconFolder />
            </span>
            <div class="row-main">
              <button class="row-title" onClick={() => openFolder(r().id)}>
                {r().title || 'Untitled folder'}
              </button>
              <span class="row-sub">
                <Show when={props.path} fallback={plural(countIn(tree()!, r().id), 'bookmark')}>
                  <Crumbs path={props.path!} onOpen={openFolder} />
                </Show>
              </span>
            </div>
          </Match>
          <Match when={r().kind === 'bookmark'}>
            <span class="tile" style={{ '--hue': tile().hue }}>
              {tile().letter}
            </span>
            <div class="row-main">
              <a class="row-title" href={r().url ?? '#'} target="_blank" rel="noopener noreferrer" draggable={false}>
                {r().title || r().url}
              </a>
              <span class="row-sub">
                <Show when={props.path} fallback={<span class="ellipsis">{hostOf(r().url) || r().url}</span>}>
                  <Crumbs path={props.path!} onOpen={openFolder} />
                </Show>
              </span>
            </div>
          </Match>
        </Switch>
        <div class="row-actions">
          <Show when={r().kind !== 'separator'}>
            <button class="icon-btn" onClick={() => editItem(r())} aria-label="Edit" title="Edit">
              <IconEdit />
            </button>
          </Show>
          <button class="icon-btn" onClick={() => setMoveIds([r().id])} aria-label="Move to folder" title="Move to folder">
            <IconMove />
          </button>
          <button class="icon-btn" onClick={() => requestDelete([r().id])} aria-label="Delete" title="Delete">
            <IconTrash />
          </button>
        </div>
      </div>
    );
  };

  const deleteSummary = () => {
    const ids = confirmDelete();
    const t = tree();
    if (!ids || !t) return '';
    const bookmarks = ids.reduce((n, id) => n + (t.rows.get(id)?.kind === 'bookmark' ? 1 : countIn(t, id)), 0);
    const name = ids.length === 1 ? `“${titleOf(t, ids[0]!)}”` : `${ids.length} items`;
    return `${name} will be removed from every connected browser${bookmarks ? `, including ${bookmarks} bookmark${bookmarks === 1 ? '' : 's'}` : ''}. You can undo right after.`;
  };

  return (
    <div class="page bookmarks-page">
      <header class="page-head">
        <div class="head-row">
          <Show when={tree() && !query()} fallback={<h1>{query() ? 'Search' : 'Bookmarks'}</h1>}>
            <nav class="crumbs-head" aria-label="Folder path">
              <For each={exists() ? pathTo(tree()!, folder()) : ['root:toolbar']}>
                {(id, i) => (
                  <>
                    <Show when={i() > 0}>
                      <span class="crumb-sep">/</span>
                    </Show>
                    <DropCrumb id={id} last={i() === pathTo(tree()!, exists() ? folder() : 'root:toolbar').length - 1} onOpen={openFolder} />
                  </>
                )}
              </For>
            </nav>
          </Show>
          <div class="head-actions">
            <button class="btn" onClick={() => newItem('folder')} disabled={!state.ready()}>
              <IconFolder /> New folder
            </button>
            <button class="btn primary" onClick={() => newItem('bookmark')} disabled={!state.ready()}>
              <IconPlus /> Add bookmark
            </button>
          </div>
        </div>
        <label class="search">
          <IconSearch />
          <input type="search" placeholder="Search all bookmarks by title or address" value={draft()} onInput={(e) => onSearch(e.currentTarget.value)} aria-label="Search bookmarks" />
        </label>
      </header>

      <Show when={state.loadError()}>
        <div class="notice bad" role="alert">
          <strong>Couldn’t start syncing.</strong> {state.loadError()} <button class="link" onClick={() => location.reload()}>Try again</button>
        </div>
      </Show>

      <Show when={(state.status()?.conflicts.length ?? 0) > 0}>
        <div class="notice warn">
          <div class="notice-row">
            <span>
              {state.status()!.conflicts.length} conflicting change{state.status()!.conflicts.length === 1 ? ' was' : 's were'} resolved automatically. The most recent edit wins.
            </span>
            <span class="notice-actions">
              <button class="link" onClick={() => setShowConflicts(!showConflicts())}>
                {showConflicts() ? 'Hide' : 'Review'}
              </button>
              <button class="link" onClick={() => void clearConflicts()}>
                Dismiss
              </button>
            </span>
          </div>
          <Show when={showConflicts()}>
            <ul class="conflict-list">
              <For each={state.status()!.conflicts}>
                {(c) => {
                  const by = () => state.devices()?.find((d) => d.id === c.byDevice)?.name ?? null;
                  return (
                    <li>
                      {describeConflict(c, by())} <span class="muted">{new Date(c.at).toLocaleString()}</span>
                    </li>
                  );
                }}
              </For>
            </ul>
          </Show>
        </div>
      </Show>

      <Show when={selected().size > 0}>
        <div class="bulk" role="toolbar" aria-label="Selection">
          <span>{selected().size} selected</span>
          <button class="btn" onClick={() => setMoveIds(children().filter((c) => selected().has(c)))}>
            <IconMove /> Move to…
          </button>
          <button class="btn danger" onClick={() => requestDelete(children().filter((c) => selected().has(c)))}>
            <IconTrash /> Delete
          </button>
          <button class="link" onClick={() => setSelected(new Set<string>())}>
            Clear
          </button>
        </div>
      </Show>

      <Switch>
        <Match when={!state.ready() && !state.loadError()}>
          <div class="list" aria-busy="true">
            <For each={[0, 1, 2, 3, 4, 5]}>{() => <div class="row skeleton"><span class="tile" /><div class="row-main"><span class="bar w60" /><span class="bar w30" /></div></div>}</For>
          </div>
        </Match>
        <Match when={query().trim()}>
          <Show
            when={hits().length}
            fallback={
              <div class="empty">
                <h2>No matches for “{query()}”</h2>
                <p>Search looks at titles, addresses and folder names across every folder.</p>
              </div>
            }
          >
            <p class="result-count">
              {hits().length === 200 ? 'Showing the first 200 matches' : `${hits().length} match${hits().length === 1 ? '' : 'es'}`}
            </p>
            <div class="list">
              <For each={hits()}>{(h) => <Row row={h.row} path={h.path} />}</For>
            </div>
          </Show>
        </Match>
        <Match when={libraryEmpty()}>
          <Onboarding onAdd={() => newItem('bookmark')} />
        </Match>
        <Match when={!exists()}>
          <div class="empty">
            <h2>This folder no longer exists</h2>
            <p>It may have been deleted on another device.</p>
            <button class="btn" onClick={() => openFolder('root:toolbar')}>
              Go to Bookmarks bar
            </button>
          </div>
        </Match>
        <Match when={children().length === 0}>
          <div
            class="empty drop-zone"
            classList={{ active: canDropInto(folder(), dnd.dragging()) }}
            onDragOver={(e) => canDropInto(folder(), dnd.dragging()) && e.preventDefault()}
            onDrop={onListDrop}
          >
            <h2>Nothing in {titleOf(tree()!, folder())} yet</h2>
            <p>Add a bookmark, or drag items here from another folder.</p>
          </div>
        </Match>
        <Match when={true}>
          <div class="list" onDragOver={(e) => canDropInto(folder(), dnd.dragging()) && e.preventDefault()} onDrop={onListDrop}>
            <For each={children()}>{(id) => <Show when={tree()!.rows.get(id)}>{(row) => <Row row={row()} />}</Show>}</For>
          </div>
          <p class="hint">Drag rows to reorder, or onto a folder to move them. Changes reach your browsers within seconds.</p>
        </Match>
      </Switch>

      <EditDialog target={editTarget()} tree={tree()} onClose={() => setEditTarget(null)} onSave={save} />
      <MoveDialog
        ids={moveIds()}
        tree={tree()}
        initial={folder()}
        onClose={() => setMoveIds(null)}
        onMove={async (target) => {
          const ids = moveIds()!;
          await edit((ed) => ed.move(ids, target));
          setSelected(new Set<string>());
          toast(`Moved to ${titleOf(tree()!, target)}.`, { action: { label: 'Open', run: () => openFolder(target) } });
        }}
      />
      <ConfirmDialog
        open={!!confirmDelete()}
        title="Delete from all browsers?"
        body={<p>{deleteSummary()}</p>}
        confirm="Delete"
        danger
        onClose={() => setConfirmDelete(null)}
        onConfirm={() => doDelete(confirmDelete()!)}
      />
    </div>
  );
}

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

function DropCrumb(props: { id: string; last: boolean; onOpen: (id: string) => void }) {
  const [over, setOver] = createSignal(false);
  return (
    <button
      class="crumb"
      classList={{ last: props.last, over: over() }}
      onClick={() => props.onOpen(props.id)}
      aria-current={props.last ? 'page' : undefined}
      onDragOver={(e) => {
        if (canDropInto(props.id, dnd.dragging())) {
          e.preventDefault();
          setOver(true);
        }
      }}
      onDragLeave={() => setOver(false)}
      onDrop={async (e) => {
        e.preventDefault();
        setOver(false);
        const ids = draggedIds(e);
        endDrag();
        if (ids && canDropInto(props.id, ids)) await edit((ed) => ed.move(ids, props.id));
      }}
    >
      {titleOf(state.tree()!, props.id)}
    </button>
  );
}

function Crumbs(props: { path: string[]; onOpen: (id: string) => void }) {
  return (
    <span class="path">
      <For each={props.path}>
        {(id, i) => (
          <>
            <Show when={i() > 0}>
              <span class="crumb-sep">/</span>
            </Show>
            <button class="path-link" onClick={() => props.onOpen(id)}>
              {titleOf(state.tree()!, id)}
            </button>
          </>
        )}
      </For>
    </span>
  );
}

function Onboarding(props: { onAdd: () => void }) {
  return (
    <div class="empty onboarding">
      <h2>Bring in your bookmarks</h2>
      <p>Install bsync in each browser you use and sign in with the same Google account. Existing bookmarks are merged, never duplicated.</p>
      <div class="onboard-actions">
        <Show when={config.chromeExtensionUrl} fallback={<span class="muted">Chrome extension: see the README for loading it unpacked.</span>}>
          <a class="btn" href={config.chromeExtensionUrl} target="_blank" rel="noopener">
            Get it for Chrome <IconExternal />
          </a>
        </Show>
        <Show when={config.firefoxExtensionUrl}>
          <a class="btn" href={config.firefoxExtensionUrl} target="_blank" rel="noopener">
            Get it for Firefox <IconExternal />
          </a>
        </Show>
        <button class="btn primary" onClick={props.onAdd}>
          <IconPlus /> Add a bookmark here
        </button>
      </div>
      <p class="muted small">
        Already installed? Check <A href="/devices">Devices</A> to see which browsers are connected.
      </p>
    </div>
  );
}
